import { styled } from "@mui/material/styles"
import { useCallback } from "react"

import ButtonDialog from "#common/Dialog/ButtonsDialog"
import { Stack } from "@mui/material"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"

export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialogContent-root": {
		padding: theme.spacing(2),
		overflowY: "auto",
	},
	"& .MuiDialogActions-root": {
		padding: theme.spacing(1),
	},
	"& .MuiPaper-root": {
		borderRadius: theme.shape.borderRadius * 5, // más redondeado
		boxShadow: theme.shadows[5], // sombra más notoria
		backgroundColor: theme.palette.background.paper, // puedes cambiar color de fondo
	},
}))

export const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	padding: "2vw !important",
	// Estilos personalizados para el scrollbar
	"&::-webkit-scrollbar": {
		width: "4px",
	},
	"&::-webkit-scrollbar-thumb": {
		backgroundColor: theme.palette.primary.main,
		borderRadius: "6px",
	},
	"&::-webkit-scrollbar-track": {
		backgroundColor: theme.palette.background.default,
		borderRadius: "10px",
	},
}))

/**
 * Componente Dialog personalizado que gestiona un diálogo modal con título, contenido y acciones.
 * Proporciona una interfaz consistente con botones de cancelar y enviar.
 *
 * @component
 * @example
 * const [open, setOpen] = useState(false)
 *
 * return (
 *   <DialogMaster
 *     open={open}
 *     setOpen={setOpen}
 *     title="Crear Colaborador"
 *     handleSubmit={() => console.log('enviado')}
 *   >
 *     <FormElement />
 *   </DialogMaster>
 * )
 *
 * @param {Object} props - Propiedades del componente
 * @param {string} props.title - Título que aparece en la parte superior del diálogo
 * @param {boolean} props.open - Controla si el diálogo está abierto o cerrado
 * @param {(open: boolean) => void} props.setOpen - Función para cambiar el estado del diálogo
 * @param {React.ReactNode} props.children - Contenido que se muestra dentro del diálogo
 * @param {() => void} [props.onClose] - Callback opcional que se ejecuta al cerrar el diálogo
 * @param {() => void | Promise<void>} [props.handleSubmit] - Callback para el botón enviar. Si existe, el botón se muestra
 * @param {boolean} [props.disabled=false] - Bloquea todos los botones (cancelar y enviar)
 * @param {boolean} [props.disabledSubmit=false] - Bloquea solo el botón enviar
 * @param {"xs"|"sm"|"md"|"lg"|"xl"} [props.maxWidth="xl"] - Ancho máximo del diálogo
 * @param {Object} [props.propsTitle] - Propiedades adicionales para el DialogTitle
 * @param {Object} [props.propsChildren] - Propiedades adicionales para el DialogContent
 * @returns {React.ReactElement} El componente Dialog
 */
export function DialogMaster({ title, setOpen, children, onClose, ...props }) {
	const closeDialog = useCallback(() => {
		if (onClose && typeof onClose === "function") onClose()
		setOpen(false)
	}, [onClose, setOpen])

	return (
		<BootstrapDialog
			{...{
				fullWidth: true,
				maxWidth: "xl",
			}}
			onClose={closeDialog}
			{...props}
		>
			<DialogMasterTitle title={title} />
			<DialogMasterContent>{children}</DialogMasterContent>
			<DialogMasterButtons onClose={closeDialog} {...props} />
		</BootstrapDialog>
	)
}

/**
 * Renderiza el título del diálogo
 * @private
 * @param {Object} props
 * @param {string} [props.title] - Texto del título
 * @param {Object} [props.propsTitle] - Props adicionales para DialogTitle
 * @returns {React.ReactElement|null}
 */
const DialogMasterTitle = ({ propsTitle, title }) => {
	if (!title) return null
	return (
		<DialogTitle
			id="Dialog-Form"
			textAlign="center"
			textTransform={"capitalize"}
			{...propsTitle}
		>
			{title}
		</DialogTitle>
	)
}

/**
 * Renderiza el contenido del diálogo
 * @private
 * @param {Object} props
 * @param {React.ReactNode} [props.children] - Contenido a mostrar
 * @param {Object} [props.propsChildren] - Props adicionales para CustomDialogContent
 * @returns {React.ReactElement|null}
 */
const DialogMasterContent = ({ children, propsChildren }) => {
	if (!children) return null
	return (
		<CustomDialogContent dividers {...propsChildren}>
			{children}
		</CustomDialogContent>
	)
}

/**
 * Renderiza los botones de acción del diálogo (cancelar y enviar)
 * @private
 * @param {Object} props
 * @param {() => void} props.onClose - Callback para cerrar el diálogo
 * @param {() => void | Promise<void>} [props.handleSubmit] - Callback para enviar
 * @param {boolean} [props.disabled=false] - Deshabilita todos los botones
 * @param {boolean} [props.disabledSubmit=false] - Deshabilita solo el botón enviar
 * @returns {React.ReactElement}
 */
const DialogMasterButtons = ({ onClose, ...props }) => {
	const { handleSubmit, disabled = false, disabledSubmit = false } = props
	const buttons = [
		{
			label: `cancelar`,
			onClick: onClose,
			disabled,
		},
	]
	if (handleSubmit && typeof handleSubmit === "function")
		buttons.push({
			label: `enviar`,
			onClick: handleSubmit,
			disabled: disabledSubmit || disabled,
		})

	return (
		<DialogActions sx={{ alignContent: "center", justifyContent: "center" }}>
			<Stack direction="row" spacing={3}>
				{buttons.map((button) => (
					<ButtonDialog key={`buttonDialog${button.label}`} {...button} />
				))}
			</Stack>
		</DialogActions>
	)
}
